import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { GuideCardGrid, GuideFooter, GuideSteps } from "@/components/guide/GuideSections";
import { JsonLd } from "@/components/JsonLd";
import { KeyTakeaways } from "@/components/KeyTakeaways";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ReviewedBy } from "@/components/ReviewedBy";
import { SITE } from "@/content/site";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { faqSchema } from "@/lib/schema";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Resources", path: "/resources" },
  { name: "What Is Digital Marketing", path: "/what-is-digital-marketing" },
];

const CHANNELS = [
  { t: "Search engine optimization (SEO)", d: "Earning unpaid Google traffic by matching pages to what people search for. Slowest to start, cheapest to sustain.", ex: "A Dhaka furniture brand ranking for \"office chair price in bangladesh\"." },
  { t: "Paid search and social ads", d: "Buying attention on Google, Meta or TikTok. Traffic stops the day the budget stops.", ex: "Boosting a Facebook post to a 25–34 Dhaka audience for a Friday sale." },
  { t: "Content marketing", d: "Publishing guides, videos and comparisons that answer buyer questions before the sales call.", ex: "A B2B SaaS publishing \"how to file VAT online in Bangladesh\"." },
  { t: "Email and SMS", d: "The only channel you own outright. Best return per taka once you have a list worth mailing.", ex: "An abandoned-cart email that reminds shoppers what they left behind." },
  { t: "Social and creator marketing", d: "Building an audience on the platforms your buyers already scroll, or renting someone else's.", ex: "A skincare brand paying three Bangladeshi creators for honest reviews." },
  { t: "AEO and GEO", d: "Getting quoted by ChatGPT, Perplexity, Gemini and Google AI Overviews — the newest channel, and still the least crowded.", ex: "Being the source an AI cites when someone asks it to recommend an agency." },
];

const BENEFITS = [
  { t: "You can measure it", d: "Unlike a billboard, every click, form and purchase is attributable. You learn which taka worked." },
  { t: "It scales down as well as up", d: "A ৳5,000 test tells you whether ৳500,000 is worth spending. Traditional media rarely allows that." },
  { t: "It compounds", d: "Ads stop when the budget stops. SEO pages, email lists and AI citations keep working after you stop paying." },
  { t: "It reaches beyond your city", d: "The same site that sells in Rangpur sells in Singapore, if the search intent is there." },
];

const STEPS = [
  { t: "Pick one buyer and one outcome", d: "\"More traffic\" is not a goal. \"30 qualified demo requests a month from ecommerce founders\" is." },
  { t: "Fix the destination first", d: "Speed, mobile layout, clear pricing, working forms. Traffic to a broken page is money set on fire." },
  { t: "Choose one paid channel and one owned channel", d: "Usually ads for immediate signal, SEO or email for compounding return. Two, not six." },
  { t: "Set up measurement before you spend", d: "GA4, conversion events, and a call or form tracker. Without this you're guessing in month three." },
  { t: "Run 90-day cycles", d: "Ship, measure, kill what didn't work. Most programs fail from switching too early, not from picking wrong." },
];

const FAQS = [
  {
    q: "What is digital marketing?",
    a: "Digital marketing is the practice of promoting products or services through internet-connected channels — search engines, websites, email, social media, and now AI assistants. It differs from traditional marketing in that every interaction can be measured, tested and attributed to revenue.",
  },
  {
    q: "What are the main types of digital marketing?",
    a: "The main types are SEO, paid search and social advertising, content marketing, email and SMS marketing, social and creator marketing, affiliate marketing, and — newest — AEO and GEO, which target AI answer engines like ChatGPT and Google AI Overviews.",
  },
  {
    q: "ডিজিটাল মার্কেটিং কি? (What is digital marketing in Bangla?)",
    a: "ডিজিটাল মার্কেটিং হলো ইন্টারনেট ব্যবহার করে পণ্য বা সেবা প্রচার করার পদ্ধতি — যেমন গুগল সার্চ (SEO), ফেসবুক ও গুগল বিজ্ঞাপন, কনটেন্ট মার্কেটিং, ইমেইল মার্কেটিং এবং এখন ChatGPT-এর মতো এআই অ্যাসিস্ট্যান্টে দৃশ্যমান হওয়া। এর সবচেয়ে বড় সুবিধা হলো প্রতিটি খরচের ফলাফল মাপা যায়।",
  },
  {
    q: "How much does digital marketing cost in Bangladesh?",
    a: "There is no single market rate. Costs depend on the channels, how much work is included and whether ad spend is part of the budget. Start with a small, measurable test, ask every provider for a written scope, and keep ad spend separate from the fee for the work. My own current starting prices are on the pricing page.",
  },
  {
    q: "How long does digital marketing take to work?",
    a: "Paid ads start producing data as soon as they run. SEO and content take longer and have no fixed timeline — it depends on your site, competition and how much work ships. Email can work quickly once you already have a list. Agree a baseline and review results monthly.",
  },
  {
    q: "Can I learn digital marketing on my own?",
    a: "Yes. The fundamentals — search intent, analytics, copy, offer design — are learnable from free material plus a real website to practise on. What takes longer is judgement: knowing which channel to drop, and when.",
  },
];

export const Route = createFileRoute("/what-is-digital-marketing")({
  head: () =>
    buildSeo({
      title: "What Is Digital Marketing? Definition, Types & Examples (Bangla + English)",
      description:
        "A plain-English (and Bangla) explanation of digital marketing: what it is, the main channels, real examples, costs in Bangladesh, and how to start a program that actually pays back.",
      path: "/what-is-digital-marketing",
      ogEyebrow: "Guide",
      keywords:
        "what is digital marketing, digital marketing ki, ডিজিটাল মার্কেটিং কি, digital marketing bangla, types of digital marketing, digital marketing examples, digital marketing Bangladesh",
      // True Bangla counterpart: same topic, full translation.
      alternates: { bn: "/bn/blog/digital-marketing-ki" },
    }),
  component: WhatIsDigitalMarketingPage,
});

function WhatIsDigitalMarketingPage() {
  return (
    <>
      <Breadcrumbs items={CRUMBS} />

      <Section headingLevel={1}
        eyebrow="Guide"
        title={<>What is <span className="text-gradient">digital marketing</span>?</>}
        subtitle="Digital marketing is promoting a product or service through internet-connected channels — search, websites, email, social platforms and AI assistants — where every interaction can be measured and tied back to revenue."
      >
        <div className="max-w-3xl">
          <KeyTakeaways
            items={[
              "Digital marketing = reaching buyers through internet channels, with measurable results per taka spent.",
              "The core channels are SEO, paid ads, content, email/SMS, social, and the newer AEO/GEO work aimed at AI assistants.",
              "Ads buy speed; SEO, email and AI citations buy compounding return. Most programs need one of each.",
              "Budgets vary widely in Bangladesh — compare written scopes, start with a small measurable test, and keep ad spend separate from fees.",
              "Ads give data quickly; SEO and content take longer with no guaranteed timeline, so track progress against a baseline.",
            ]}
          />
          <ReviewedBy
            reviewedOn="2026-08-17"
            credentials="SEO, AEO and GEO consultant based in Rangpur, Bangladesh"
          />
        </div>
      </Section>

      <Section eyebrow="Channels" title="The main types of digital marketing">
        <GuideCardGrid items={CHANNELS} reveal
          extra={(c) => <p className="mt-3 text-sm text-muted-foreground/80 italic">Example: {c.ex}</p>}
         />
      </Section>

      <Section eyebrow="Why it matters" title="What digital marketing gives you that traditional media doesn't">
        <GuideCardGrid items={BENEFITS} />
      </Section>

      <Section eyebrow="Getting started" title="How to start digital marketing, in order">
        <GuideSteps steps={STEPS} />
      </Section>

      <Section eyebrow="বাংলা" title="ডিজিটাল মার্কেটিং কি?">
        <div lang="bn" className="max-w-3xl space-y-5 text-muted-foreground leading-relaxed -mt-4">
          <p>
            <strong className="text-foreground">ডিজিটাল মার্কেটিং</strong> হলো ইন্টারনেট ব্যবহার করে
            পণ্য বা সেবা প্রচার করার পদ্ধতি। গুগলে সার্চ করলে যে ওয়েবসাইটগুলো আসে, ফেসবুকে যে
            বিজ্ঞাপন দেখেন, ইমেইলে যে অফার পান — এই সবই ডিজিটাল মার্কেটিংয়ের অংশ।
          </p>
          <div>
            <h3 className="font-display text-lg text-foreground">ডিজিটাল মার্কেটিং কত প্রকার?</h3>
            <ul className="mt-3 space-y-2 list-disc pl-5">
              <li><strong className="text-foreground">এসইও (SEO)</strong> — গুগলে বিনা খরচে র‍্যাংক করা।</li>
              <li><strong className="text-foreground">পেইড অ্যাডস</strong> — গুগল ও ফেসবুক বিজ্ঞাপন।</li>
              <li><strong className="text-foreground">কনটেন্ট মার্কেটিং</strong> — ব্লগ, ভিডিও ও গাইড।</li>
              <li><strong className="text-foreground">ইমেইল ও এসএমএস মার্কেটিং</strong> — নিজের গ্রাহক তালিকায় সরাসরি পৌঁছানো।</li>
              <li><strong className="text-foreground">এআই সার্চ অপটিমাইজেশন (AEO/GEO)</strong> — ChatGPT ও Gemini-তে আপনার ব্র্যান্ডের নাম আসা।</li>
            </ul>
          </div>
          <div>
            <h3 className="font-display text-lg text-foreground">বাংলাদেশে খরচ কেমন?</h3>
            <p className="mt-2">
              খরচের কোনো একক বাজারদর নেই — কোন চ্যানেল, কতটুকু কাজ এবং বিজ্ঞাপনের বাজেট
              আলাদা কি না, তার ওপর নির্ভর করে। ছোট, মাপা যায় এমন পরীক্ষা দিয়ে শুরু করুন, প্রতিটি
              প্রোভাইডারের কাছে লিখিত স্কোপ চান, এবং বিজ্ঞাপনের বাজেট ফি থেকে আলাদা রাখুন।
            </p>
          </div>
          <div>
            <h3 className="font-display text-lg text-foreground">কত দিনে ফল পাওয়া যায়?</h3>
            <p className="mt-2">
              পেইড বিজ্ঞাপন চালু হলেই ডেটা আসতে শুরু করে। এসইও ও কনটেন্টে সময় বেশি লাগে এবং
              কোনো নির্দিষ্ট সময়সীমা নেই — সাইটের অবস্থা, প্রতিযোগিতা ও কতটুকু কাজ হচ্ছে তার
              ওপর নির্ভর করে। শুরুতে একটি বেসলাইন ঠিক করে প্রতি মাসে অগ্রগতি মিলিয়ে দেখুন।
            </p>
          </div>
          <p>
            বাংলায় আরও লেখা পড়তে{" "}
            <Link to="/bn" className="text-primary underline underline-offset-4">
              বাংলা সেকশন
            </Link>{" "}
            দেখুন।
          </p>
        </div>
      </Section>

      <GuideFooter
        nextTitle="Where to go from here"
        links={[
          { label: "Digital marketing services", to: "/services/digital-marketing" },
          { label: "SEO expert in Bangladesh", to: "/seo-expert-bangladesh" },
          { label: "Marketing glossary", to: "/glossary" },
          { label: "Blog", to: "/blog" },
        ]}
        aiContext="digital marketing"
        aiQuestions={["what-is-aeo", "aeo-vs-seo-vs-geo", "seo-bangladesh-market", "bangla-seo-worth-it"]}
        faqs={FAQS}
        faqIdPrefix="faq-what-is-dm"
        ctaTitle={<>Want help turning this into an actual <span className="text-gradient">plan</span>?</>}
      />

      <JsonLd
        data={faqSchema(FAQS)}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "DefinedTerm",
          name: "Digital marketing",
          description:
            "Digital marketing is the promotion of products or services through internet-connected channels — search engines, websites, email, social media and AI assistants — where interactions can be measured and attributed to revenue.",
          inDefinedTermSet: `${SITE.url}/glossary`,
          url: `${SITE.url}/what-is-digital-marketing`,
        }}
      />
      <JsonLd data={breadcrumbSchema(CRUMBS)} />
    </>
  );
}
